import { useState, useRef, useMemo } from 'react';
import '@/App.css';
import { LogOut, ShieldCheck, Lock } from 'lucide-react';
import { Toaster } from 'sonner';
import PreorderScreen from './components/PreorderScreen';
import OrderReportScreen from './components/OrderReportScreen';
import PickupScreen from './components/PickupScreen';
import AdminScreen from './components/AdminScreen';
import IncomingScreen from './components/IncomingScreen';
import BookingsScreen from './components/BookingsScreen';
import LoginScreen from './components/LoginScreen';
import AdminUnlockModal from './components/AdminUnlockModal';
import KioskApp from './kiosk/KioskApp';
import PublicPlanScreen from './kiosk/PublicPlanScreen';
import BookingApp, { BookingSuccess } from './booking/BookingApp';
import AccountApp from './account/AccountApp';
import WellnessAdminGate from './wellness-admin/WellnessAdminApp';
import HomePage from './website/HomePage';
import WellnessClubPage from './website/WellnessClubPage';
import { PrivacyPolicyPage, TermsOfServicePage, RefundPolicyPage } from './website/PolicyPages';
import { AuthProvider, useAuth } from './AuthContext';

const STAFF_TABS = [
  { id: 'preorder', label: 'Preorder' },
  { id: 'pickup',   label: 'Pickup' },
  { id: 'incoming', label: 'Incoming' },
  { id: 'bookings', label: 'Bookings' },
];
const ADMIN_TABS = [
  ...STAFF_TABS,
  { id: 'report', label: 'Report' },
  { id: 'admin',  label: 'Admin' },
];

const TabButton = ({ id, label, active, onClick }) => (
  <button
    data-testid={`tab-${id}`}
    onClick={onClick}
    className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-semibold text-xs sm:text-sm tracking-wider uppercase transition-colors whitespace-nowrap ${
      active
        ? 'border-[#0066FF] text-[#0066FF]'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    {label}
  </button>
);

const AppShell = () => {
  const { logout, isAdmin } = useAuth();
  const tabs = useMemo(() => (isAdmin ? ADMIN_TABS : STAFF_TABS), [isAdmin]);
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [adminUnlockOpen, setAdminUnlockOpen] = useState(false);
  const touchStart = useRef(null);

  // If the user is downgraded (e.g., signed out from admin to staff),
  // make sure the active tab is still allowed.
  if (!tabs.find((t) => t.id === activeTab)) {
    setActiveTab(tabs[0].id);
  }

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    const tag = (e.target.tagName || '').toLowerCase();
    if (['input', 'textarea', 'select'].includes(tag)) {
      touchStart.current = null;
      return;
    }
    touchStart.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    touchStart.current = null;

    const THRESHOLD = 60;
    if (Math.abs(dx) < THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    const ids = tabs.map((t) => t.id);
    const idx = ids.indexOf(activeTab);
    if (dx < 0 && idx < ids.length - 1) setActiveTab(ids[idx + 1]);
    else if (dx > 0 && idx > 0) setActiveTab(ids[idx - 1]);
  };

  return (
    <div
      className="min-h-screen bg-[#F3F4F6]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-6">
          <div className="flex items-center justify-between gap-2">
            <div className="flex space-x-2 sm:space-x-8 overflow-x-auto">
              {tabs.map((t) => (
                <TabButton
                  key={t.id}
                  id={t.id}
                  label={t.label}
                  active={activeTab === t.id}
                  onClick={() => setActiveTab(t.id)}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {isAdmin ? (
                <span
                  data-testid="admin-badge"
                  className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1f3d2b] bg-[#1f3d2b]/10 px-2 py-1 rounded-full"
                >
                  <ShieldCheck size={12} strokeWidth={2.5} /> Admin
                </span>
              ) : (
                <button
                  data-testid="unlock-admin-button"
                  onClick={() => setAdminUnlockOpen(true)}
                  title="Unlock admin"
                  className="inline-flex items-center gap-1 text-xs sm:text-sm text-[#d97736] hover:text-[#a85820] font-semibold px-2 py-2 rounded transition-colors"
                >
                  <Lock size={14} />
                  <span className="hidden sm:inline">Unlock Admin</span>
                </button>
              )}
              <button
                data-testid="logout-button"
                onClick={logout}
                title="Sign out"
                className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-gray-900 px-2 py-2 rounded transition-colors"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-2 sm:p-4 lg:p-6">
        {activeTab === 'preorder' && <PreorderScreen />}
        {activeTab === 'pickup' && <PickupScreen />}
        {activeTab === 'incoming' && <IncomingScreen />}
        {activeTab === 'bookings' && <BookingsScreen />}
        {isAdmin && activeTab === 'report' && <OrderReportScreen />}
        {isAdmin && activeTab === 'admin' && <AdminScreen />}
      </div>

      <AdminUnlockModal
        open={adminUnlockOpen}
        onClose={() => setAdminUnlockOpen(false)}
      />

      <Toaster
        position="top-center"
        richColors
        closeButton
        toastOptions={{
          style: { fontFamily: "'Manrope', sans-serif" },
        }}
      />
    </div>
  );
};

const Gate = () => {
  const { status } = useAuth();
  if (status === null) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }
  return status ? <AppShell /> : <LoginScreen />;
};

const App = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  if (path.startsWith('/kiosk')) return <KioskApp />;
  if (path.startsWith('/plan/')) return <PublicPlanScreen />;
  if (path.startsWith('/account')) return <AccountApp />;
  if (path.startsWith('/wellness-admin')) {
    return (
      <AuthProvider>
        <WellnessAdminGate />
      </AuthProvider>
    );
  }
  // Booking success route — must come BEFORE the catch-all /booking route.
  if (path.startsWith('/booking/success')) return <BookingSuccess />;
  if (path.startsWith('/booking')) return <BookingApp />;
  if (path.startsWith('/wellness-suite')) return <WellnessClubPage />;
  if (path.startsWith('/privacy-policy')) return <PrivacyPolicyPage />;
  if (path.startsWith('/terms')) return <TermsOfServicePage />;
  if (path.startsWith('/refund-policy')) return <RefundPolicyPage />;

  // Staff app — accessible at /staff (moved from /)
  if (path.startsWith('/staff')) {
    return (
      <AuthProvider>
        <Gate />
      </AuthProvider>
    );
  }

  // Public website homepage at /
  return <HomePage />;
};

export default App;
